import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useResetPassword } from '@/hooks/useAuth';
import { getApiErrorMessage } from '@/api/client';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';

const MIN_PASSWORD_LENGTH = 8;

/** True when the API says the emailed token is unknown, used or expired. */
function isTokenError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const errors = (error.response?.data as { errors?: Record<string, string[]> } | undefined)?.errors;
  return Boolean(errors?.token);
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const resetPassword = useResetPassword();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldError, setFieldError] = useState<string>();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setFieldError(`Geslo mora imeti vsaj ${MIN_PASSWORD_LENGTH} znakov.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setFieldError('Gesli se ne ujemata.');
      return;
    }
    setFieldError(undefined);
    const ok = await resetPassword.mutateAsync({ token, newPassword }).then(() => true).catch(() => false);
    if (ok) navigate('/login', { replace: true, state: { notice: 'Geslo je spremenjeno. Prijavite se z novim geslom.' } });
  }

  if (!token || isTokenError(resetPassword.error)) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-lg font-semibold text-fg">Povezava ni veljavna</h1>
        <Alert variant="error">
          Povezava za ponastavitev gesla je neveljavna, je že bila uporabljena ali je potekla. Povezave veljajo eno uro
          in delujejo samo enkrat.
        </Alert>
        <Link to="/forgot-password" className="text-center text-sm font-medium text-accent-text hover:underline">
          Pošlji novo povezavo
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-semibold text-fg">Novo geslo</h1>
        <p className="mt-1 text-sm text-fg-muted">Izberite novo geslo za svoj račun. Po spremembi vas bomo odjavili povsod.</p>
      </div>

      {resetPassword.isError && <Alert variant="error">{getApiErrorMessage(resetPassword.error)}</Alert>}

      <Input
        label="Novo geslo"
        type="password"
        name="new-password"
        autoComplete="new-password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        error={fieldError}
      />
      <Input
        label="Ponovite novo geslo"
        type="password"
        name="confirm-password"
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />

      <Button type="submit" isLoading={resetPassword.isPending} disabled={!newPassword || !confirmPassword} className="w-full">
        Nastavi geslo
      </Button>
    </form>
  );
}
